import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Avatar,
  Paper,
  Divider,
  Button,
} from '@mui/material';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import GroupsIcon from '@mui/icons-material/Groups';
import '../App.css';

const testimonials = [
  {
    id: 1,
    name: 'Superintendent',
    role: 'Educational Leadership',
    organization: 'Kansas State School for the Blind',
    icon: <SchoolIcon />,
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    accentColor: '#667eea',
    featured: true,
    quotes: [
      "We believe that the BSP has the potential to make a significant impact on our students' tactile literacy and overall learning experience.",
      "The BSP's innovative design and gamified approach align nicely with our mission to foster a love of learning through hands-on exploration and play. We are particularly impressed by the puzzle's versatility and adaptability for diverse age groups and learning environments.",
      "We envision several ways to incorporate the BSP into our existing programs: Makerspace: The BSP would be a valuable addition to our Makerspace, providing students with an opportunity to explore Braille and tactile learning through hands-on activities and creative projects. Mobile STEM Unit: The puzzle's portability makes it ideal for inclusion in our Mobile STEM Unit, allowing us to bring tactile learning experiences to students in various settings. Family Activity Boxes: The BSP could be incorporated into our Family Activity Boxes, encouraging families to engage in tactile learning together at home. Preschool Program: The BSP's gamified activities are well-suited for our Preschool Program, introducing young children to Braille and fostering early literacy skills in an engaging way.",
      "We are excited by the potential of the BSP to enhance tactile literacy and learning across various programs at our school.",
      "I am particularly excited about the BSP's potential to create a positive association with Braille practice. By making learning fun and rewarding, the BSP can help break down barriers and empower individuals to embrace Braille as a valuable tool for lifelong learning and independence.",
      "I am confident that the Braille Skills Puzzle project will make a significant contribution to the field of tactile education and accessibility. I wholeheartedly support this initiative and encourage others to do the same.",
      "This innovative tactile learning tool holds real promise for enhancing Braille literacy and accessibility for individuals of all ages. The BSP's gamified approach to learning is particularly noteworthy. By transforming Braille practice into an engaging and enjoyable pastime, the BSP has the potential to improve how students interact with Braille. The puzzle's design, which combines tactile exploration, shape recognition, and numeracy, can be adapted to a variety of learning styles and environments.",
      "I am pleased to share that teachers at the Kansas State School for the Blind are currently testing the Braille Skills Puzzle in their classrooms.",
      "For children, the BSP offers a fun and accessible introduction to Braille, fostering early literacy skills and building confidence in their abilities. For adults who are losing their sight, the BSP could provide a low-pressure, supportive environment to learn Braille and regain tactile confidence.",
      "The puzzle's versatility for both group and independent activities further enhances its value, promoting social engagement and emotional resilience during a challenging transition."
    ],
    applications: [
      "Makerspace integration for hands-on tactile learning",
      "Mobile STEM Unit for diverse learning environments", 
      "Family Activity Boxes for home engagement",
      "Preschool Program for early literacy development"
    ],
    impact: "Currently Testing in Classrooms"
  },
  {
    id: 2,
    name: 'Vocational Rehabilitation Specialist',
    role: 'Adult Learning Expert (Completely blind since birth)',
    organization: 'Oregon Commission for the Blind',
    icon: <WorkIcon />,
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    accentColor: '#f093fb',
    featured: false,
    quotes: [
      "Since many adults have not had the opportunity to learn Braille, methods and instruction are limited. There is a true need for instructional aids to assist them in learning. Joshua Howell's Braille Skills Puzzle is one such tool that can assist adults (and children) to begin their tactile readiness and Braille studies.",
      "Since numbers are pervasive in our everyday life, e.g. elevator buttons, room signs, phone numbers, addresses, ZIP codes, etc., it is essential that students of all ages be comfortable reading numbers.",
      "Mr. Howell has provided many ideas for exploratory learning for all levels from primary aged children to adults.",
      "Joshua has outlined his mission to provide educational opportunities, materials and resources and instructional suggestions to promote the use of Braille. He has studied the topic extensively, reached out to many sectors of our community, and explored the possibilities from numerous angles. He has combined research, creation, innovation and forward-thinking solutions to address a need in the Braille instructional field.",
      "... funding a project with so many applications will Contribute to the literacy and independence of blind and visually impaired individuals. With this and future puzzles, Mr. Howell plans to promote literacy and inclusiveness. His passion, commitment and drive propel him forward. Along with this drive, the many possibilities the puzzle offers will make his project a success and ensure that he is a worthy candidate for your grant funding."
    ],
    applications: [
      "Adult Braille instruction programs",
      "Tactile readiness preparation",
      "Number literacy development",
      "Community outreach initiatives"
    ],
    impact: "Addresses Critical Adult Learning Gap"
  },
  {
    id: 3,
    name: 'Career Education Instructor',
    role: 'Teaching Professional (25 years at TSVI)',
    organization: 'Texas School for the Blind and Visually Impaired',
    icon: <VisibilityIcon />,
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    accentColor: '#4facfe',
    featured: false,
    quotes: [
      "...the ideas that this puzzle present are great and offer amazing potential for furthering of Braille competency.",
      "I and a number of teachers that I spoke with see great expansive potential in this novel idea of Braille introduction.",
      "Personally, I would love to see this project have the opportunity to grow and develop into areas that can benefit not only new Braille users but also to help individuals hone their fluency, speed and learn new Braille words or learn contractions."
    ],
    applications: [
      "Braille competency development",
      "Teacher collaboration programs",
      "Fluency and speed training",
      "Contraction learning modules"
    ],
    impact: "25+ Years Teaching Experience"
  }
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut', delay } },
});

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const Feedback = () => {
  return (
    <Container sx={{ p: 0, maxWidth: '100% !important' }}>
      <Helmet>
        <title>Professional Feedback | Amaze Puzzles</title>
        <meta
          name="description"
          content="Discover what education professionals and specialists are saying about the Braille Skills Puzzle. Read testimonials from superintendents, instructors, and rehabilitation specialists."
        />
      </Helmet>

      {/* Skip link for accessibility */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          left: '-999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          zIndex: -1,
          '&:focus': { 
            left: 0, 
            width: 'auto', 
            height: 'auto', 
            p: 2, 
            zIndex: 1000, 
            bgcolor: 'white',
            color: 'black',
            textDecoration: 'none',
            border: '2px solid var(--primary-color)'
          },
        }}
      >
        Skip to main content
      </Box>

      {/* Hero Section */}
      <Box
        component={motion.section}
        {...fadeUp(0)}
        sx={{
          position: 'relative',
          py: { xs: 10, md: 14 },
          px: { xs: 2, sm: 4, md: 6 },
          background: 'var(--hero-background-gradient)',
          color: 'white',
          overflow: 'hidden',
        }}
      >
        {/* Soft blobs */}
        <Box
          sx={{
            position: 'absolute',
            top: '15%',
            right: -80,
            width: 320,
            height: 320,
            bgcolor: 'var(--hero-blur-bg)',
            borderRadius: '50%',
            filter: 'blur(60px)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '10%',
            left: -80,
            width: 280,
            height: 280,
            bgcolor: 'var(--hero-blur-bg)',
            borderRadius: '50%',
            filter: 'blur(60px)',
          }}
        />

        <Box id="main-content" sx={{ maxWidth: 1200, mx: 'auto', textAlign: 'center', position: 'relative' }}>
          <Typography variant="h2" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
            Professional Feedback
          </Typography>
          <Typography variant="h6" sx={{ mt: 2, opacity: 0.95, maxWidth: 820, mx: 'auto' }}>
            Hear from education professionals, specialists, and instructors who recognize the transformative potential of the Braille Skills Puzzle in enhancing tactile literacy and learning outcomes.
          </Typography>
        </Box>
      </Box>

      {/* Featured Testimonial - Large Format */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, sm: 4 }, background: 'var(--background-color)' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          {testimonials.filter(t => t.featured).map((testimonial) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8 }}
            >
              <Card
                elevation={0}
                sx={{
                  borderRadius: 6,
                  overflow: 'hidden',
                  background: 'var(--card-background-gradient)',
                  border: '1px solid var(--card-border)',
                  position: 'relative',
                  mb: 8,
                }}
              >
                
                <CardContent sx={{ p: { xs: 4, md: 6 }, position: 'relative', zIndex: 2 }}>
                  <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                        <Avatar
                          sx={{
                            width: 100,
                            height: 100,
                            bgcolor: testimonial.accentColor,
                            mb: 3,
                            mx: { xs: 'auto', md: 0 },
                            border: '3px solid var(--card-border)',
                          }}
                        >
                          {testimonial.icon}
                        </Avatar>
                        
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: 'var(--text-primary-dark)' }}>
                          {testimonial.name}
                        </Typography>
                        <Typography sx={{ color: 'var(--text-secondary)', mb: 2, fontSize: '1.1rem' }}>
                          {testimonial.role}
                        </Typography>
                        
                        <Chip
                          label={testimonial.impact}
                          sx={{
                            bgcolor: `${testimonial.accentColor}15`,
                            color: testimonial.accentColor,
                            fontWeight: 600,
                            border: `1px solid ${testimonial.accentColor}30`,
                          }}
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={8}>
                      <Box sx={{ position: 'relative' }}>
                        <FormatQuoteIcon 
                          sx={{ 
                            fontSize: 60, 
                            opacity: 0.3, 
                            position: 'absolute',
                            top: -20,
                            left: -10,
                            color: testimonial.accentColor,
                          }} 
                        />
                        
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 700,
                            lineHeight: 1.4,
                            fontSize: '1.1rem',
                            pl: 4,
                            mb: 4,
                            color: 'var(--text-primary-dark)'
                          }}
                        >
                          Key Insights from {testimonial.name}
                        </Typography>
                        
                        <Stack spacing={3} sx={{ pl: 4 }}>
                          {testimonial.quotes.slice(0, 3).map((quote, index) => (
                            <Typography 
                              key={index}
                              sx={{ 
                                fontStyle: 'italic',
                                lineHeight: 1.6,
                                fontSize: '1.1rem',
                                color: 'var(--text-secondary)',
                                position: 'relative',
                                pl: 2,
                                borderLeft: `3px solid ${testimonial.accentColor}`,
                              }}
                            >
                              "{quote}"
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </Box>
      </Box>

      {/* Grid Layout for Other Testimonials */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, sm: 4 } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800, 
                mb: 2, 
                color: 'var(--text-primary-dark)', 
                textAlign: 'center' 
              }}
            >
              More Professional Insights
            </Typography>
            <Typography 
              sx={{ 
                textAlign: 'center', 
                color: 'var(--text-secondary)', 
                mb: 8,
                fontSize: '1.1rem',
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Discover additional perspectives from education professionals across the country
            </Typography>
          </motion.div>

          <Grid container spacing={4}>
            {testimonials.filter(t => !t.featured).map((testimonial, index) => (
              <Grid item xs={12} md={6} key={testimonial.id}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      borderRadius: 4,
                      background: 'var(--card-background-gradient)',
                      border: '1px solid var(--card-border)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px -12px rgba(0,0,0,0.25)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {/* Gradient accent */}
                    <Box
                      sx={{
                        height: 4,
                        background: testimonial.gradient,
                      }}
                    />
                    
                    <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      {/* Header */}
                      <Box sx={{ mb: 3 }}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                          <Avatar
                            sx={{
                              bgcolor: testimonial.accentColor,
                              width: 48,
                              height: 48,
                            }}
                          >
                            {testimonial.icon}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--text-primary-dark)' }}>
                              {testimonial.name}
                            </Typography>
                            <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                              {testimonial.role}
                            </Typography>
                          </Box>
                        </Stack>
                        
                        <Chip
                          label={testimonial.impact}
                          size="small"
                          sx={{
                            bgcolor: `${testimonial.accentColor}15`,
                            color: testimonial.accentColor,
                            fontWeight: 600,
                            border: `1px solid ${testimonial.accentColor}30`,
                          }}
                        />
                      </Box>

                      {/* All Quotes */}
                      <Box sx={{ flex: 1, mb: 3 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 700, 
                            mb: 3, 
                            color: 'var(--text-primary-dark)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <StarIcon sx={{ color: '#fbbf24' }} />
                          Professional Insights
                        </Typography>
                        
                        <Stack spacing={2}>
                          {testimonial.quotes.map((quote, quoteIndex) => (
                            <Paper
                              key={quoteIndex}
                              elevation={0}
                              sx={{
                                p: 3,
                                borderRadius: 3,
                                background: 'var(--modal-background)',
                                border: '1px solid var(--card-border)',
                                position: 'relative',
                              }}
                            >
                              <FormatQuoteIcon
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  left: 8,
                                  fontSize: 16,
                                  color: testimonial.accentColor,
                                  opacity: 0.6,
                                }}
                              />
                              <Typography
                                sx={{
                                  color: 'var(--text-primary-dark)',
                                  fontStyle: 'italic',
                                  lineHeight: 1.6,
                                  pl: 2,
                                  fontSize: '0.95rem'
                                }}
                              >
                                "{quote}"
                              </Typography>
                            </Paper>
                          ))}
                        </Stack>
                      </Box>

                      {/* Applications */}
                      <Box>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 700, 
                            mb: 2, 
                            color: 'var(--text-primary-dark)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <LightbulbIcon sx={{ fontSize: 18, color: testimonial.accentColor }} />
                          Key Applications
                        </Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          {testimonial.applications.slice(0, 2).map((app, appIndex) => (
                            <Chip
                              key={appIndex}
                              label={app}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: '0.75rem',
                                borderColor: 'var(--card-border)',
                                '&:hover': {
                                  bgcolor: `${testimonial.accentColor}10`,
                                  borderColor: testimonial.accentColor,
                                },
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Comprehensive Testimonials Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, sm: 4 }, background: 'var(--background-color)' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800, 
                mb: 2, 
                color: 'var(--text-primary-dark)', 
                textAlign: 'center' 
              }}
            >
              Complete Professional Testimonials
            </Typography>
            <Typography 
              sx={{ 
                textAlign: 'center', 
                color: 'var(--text-secondary)', 
                mb: 8,
                fontSize: '1.1rem',
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              Read the full testimonials from each education professional, showcasing the comprehensive support for the Braille Skills Puzzle
            </Typography>
          </motion.div>

          <Stack spacing={6}>
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={`full-${testimonial.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 4,
                    overflow: 'hidden',
                    background: 'var(--card-background-gradient)',
                    border: '1px solid var(--card-border)',
                    '&:hover': {
                      boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                      transform: 'translateY(-4px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {/* Header with gradient */}
                  <Box
                    sx={{
                      background: testimonial.gradient,
                      p: 4,
                      color: 'white',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -30,
                        right: -30,
                        width: 150,
                        height: 150,
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '50%',
                        filter: 'blur(30px)',
                      }}
                    />
                    
                    <Grid container spacing={3} alignItems="center">
                      <Grid item>
                        <Avatar
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            width: 80,
                            height: 80,
                            border: '3px solid rgba(255,255,255,0.3)',
                          }}
                        >
                          {testimonial.icon}
                        </Avatar>
                      </Grid>
                      <Grid item xs>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                          {testimonial.name}
                        </Typography>
                        <Typography sx={{ opacity: 0.9, fontSize: '1.1rem', mb: 1 }}>
                          {testimonial.role}
                        </Typography>
                        <Typography sx={{ opacity: 0.8, fontSize: '1rem', mb: 2 }}>
                          {testimonial.organization}
                        </Typography>
                        
                        <Chip
                          label={testimonial.impact}
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            fontWeight: 600,
                            border: '1px solid rgba(255,255,255,0.3)',
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <CardContent sx={{ p: 4 }}>
                    {/* All Quotes */}
                    <Box sx={{ mb: 4 }}>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontWeight: 700, 
                          mb: 4, 
                          color: 'var(--text-primary-dark)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <FormatQuoteIcon sx={{ color: testimonial.accentColor }} />
                        Complete Testimonial
                      </Typography>
                      
                      <Stack spacing={3}>
                        {testimonial.quotes.map((quote, quoteIndex) => (
                          <Paper
                            key={quoteIndex}
                            elevation={0}
                            sx={{
                              p: 4,
                              borderRadius: 3,
                              background: 'var(--modal-background)',
                              border: `2px solid ${testimonial.accentColor}20`,
                              position: 'relative',
                              '&:hover': {
                                borderColor: `${testimonial.accentColor}40`,
                                transform: 'translateX(4px)',
                              },
                              transition: 'all 0.3s ease',
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: 4,
                                background: testimonial.gradient,
                                borderRadius: '0 2px 2px 0',
                              }}
                            />
                            
                            <Typography
                              sx={{
                                color: 'var(--text-primary-dark)',
                                fontStyle: 'italic',
                                lineHeight: 1.7,
                                fontSize: '1.05rem',
                                pl: 2,
                              }}
                            >
                              "{quote}"
                            </Typography>
                          </Paper>
                        ))}
                      </Stack>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    {/* Applications */}
                    <Box>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700, 
                          mb: 3, 
                          color: 'var(--text-primary-dark)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <LightbulbIcon sx={{ color: testimonial.accentColor }} />
                        Identified Applications & Use Cases
                      </Typography>
                      <Grid container spacing={2}>
                        {testimonial.applications.map((application, appIndex) => (
                          <Grid item xs={12} sm={6} key={appIndex}>
                            <Chip
                              label={application}
                              variant="outlined"
                              sx={{
                                width: '100%',
                                justifyContent: 'flex-start',
                                py: 2,
                                px: 3,
                                height: 'auto',
                                '& .MuiChip-label': {
                                  whiteSpace: 'normal',
                                  fontSize: '0.95rem',
                                  fontWeight: 600,
                                },
                                borderColor: testimonial.accentColor,
                                color: testimonial.accentColor,
                                '&:hover': {
                                  bgcolor: `${testimonial.accentColor}10`,
                                  borderColor: testimonial.accentColor,
                                  transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.3s ease',
                              }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Stack>
        </Box>
      </Box>
    </Container>
  );
};

export default Feedback;